import {CHAT_STREAM_URL} from "@core/api/query/chatbot";
import {
  ChatAction,
  ChatChoices,
  ChatCompletion,
  ChatCompletionRequest,
  ChatRole,
} from "@core/model";
import {Response} from "@core/model/Response";
import {store} from "@core/store";

class ChatStreamUnsupported extends Error {
  constructor() {
    super("stream unsupported");
    this.name = "ChatStreamUnsupported";
  }
}

class ChatRequestError extends Error {
  status?: number;
  code?: number;
  serverMessage?: string;

  constructor(status?: number, body?: {code?: number; message?: string}) {
    super("chatbot.error");
    this.name = "ChatRequestError";
    this.status = status;
    this.code = body && "code" in body ? body.code : status;
    this.serverMessage = body?.message;
  }
}

type SseEvent =
  | {type: "done"}
  | {type: "delta"; delta: string}
  | {type: "action"; action: ChatAction}
  | {type: "choices"; choices: ChatChoices}
  | {type: "error"; code?: number; message?: string};

type ChatErrorDescriptor = {key: string} | {text: string};

// Controlli, bidi e caratteri invisibili: una scelta non può nascondere
// un secondo comando o un URL dietro al testo del pulsante.
const UNSAFE_CHOICE =
  /[\u0000-\u001F\u007F\u200B-\u200F\u2028\u2029\u202A-\u202E\u2060-\u2069\uFEFF]|\u{E0000}-\u{E007F}/u;

// Solo stringhe visibili. Oggetti con href o action non diventano pulsanti.
const choiceOptions = (options: unknown): string[] => {
  if (!Array.isArray(options)) {
    return [];
  }
  const labels: string[] = [];
  options.forEach(option => {
    if (typeof option !== "string" || UNSAFE_CHOICE.test(option)) {
      return;
    }
    const label = option.trim();
    if (label) {
      labels.push(label);
    }
  });
  return labels;
};

// Provider e modello stanno sul tenant. Il backend rifiuta il ruolo system.
// maxTokens è opzionale e non va inviato. timeZone è il fuso del browser.
const browserTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
};

const completionBody = (request: {
  messages: {role: string; content: string}[];
}): ChatCompletionRequest => ({
  messages: request.messages
    .filter(
      (message): message is {role: ChatRole; content: string} =>
        message.role === "user" || message.role === "assistant",
    )
    .map(({role, content}) => ({role, content})),
  timeZone: browserTimeZone(),
});

// 502 è la chiave rifiutata dal provider, non un JWT scaduto.
// 503 è il provider senza chiave. 429 è il rate limit.
const chatErrorDescriptor = (error: unknown): ChatErrorDescriptor => {
  const failure =
    error instanceof ChatRequestError
      ? {
          status: error.status,
          code: error.code,
          message: error.serverMessage,
        }
      : {
          status:
            typeof (error as {status?: unknown})?.status === "number"
              ? (error as {status: number}).status
              : undefined,
          code: (error as {data?: {code?: number}})?.data?.code,
          message: (error as {data?: {message?: string}})?.data?.message,
        };
  const status = failure.status;
  const code = failure.code;
  if (status === 503 || code === 503) {
    return {key: "chatbot.error.unavailable"};
  }
  if (status === 502 || code === 502) {
    return {key: "chatbot.error.rejected"};
  }
  if (status === 429 || code === 429) {
    return {key: "chatbot.error.rateLimit"};
  }
  // 1507: nessun tenant. 1508: chatbot disattivato. Si mostra message.
  if (code === 1507 || code === 1508) {
    const accessMessage = failure.message?.trim();
    if (accessMessage) {
      return {text: accessMessage};
    }
    return {
      key:
        code === 1507 ? "chatbot.error.selectTenant" : "chatbot.error.disabled",
    };
  }
  const message = failure.message?.trim();
  if (message) {
    return {text: message};
  }
  if (code !== undefined && code !== 0) {
    return {key: `error.${code}`};
  }
  return {key: "chatbot.error.default"};
};

const errorCode = (error: unknown) => {
  if (!error) {
    return undefined;
  }
  if (error instanceof ChatRequestError) {
    return error.code;
  }
  return (error as {data?: {code?: number}})?.data?.code;
};

const isChatAccessError = (error: unknown) => {
  const code = errorCode(error);
  return code === 1507 || code === 1508;
};

const isChatDisabledError = (error: unknown) => errorCode(error) === 1508;

const isChatbotOn = (status: {enabled?: boolean} | undefined, error: unknown) =>
  status?.enabled === true && !isChatDisabledError(error);

const parseSseChunk = (buffer: string) => {
  const lines = buffer.split("\n");
  const rest = lines.pop() ?? "";
  const events = lines
    .filter(line => line.startsWith("data:"))
    .map(line => line.slice(5).trim())
    .filter(data => data.length > 0);
  return {events, rest};
};

const readSseData = (data: string): SseEvent => {
  if (data === "[DONE]") {
    return {type: "done"};
  }
  try {
    const json = JSON.parse(data) as {
      delta?: string;
      action?: ChatAction;
      choices?: ChatChoices;
      code?: number;
      message?: string;
      error?: {code?: number; message?: string} | string;
    };
    if (typeof json.delta === "string" && json.delta.length > 0) {
      return {type: "delta", delta: json.delta};
    }
    if (json.action && typeof json.action.id === "string") {
      return {type: "action", action: json.action};
    }
    if (json.choices && Array.isArray(json.choices.options)) {
      const options = choiceOptions(json.choices.options);
      if (options.length > 0) {
        return {
          type: "choices",
          choices: {
            question:
              typeof json.choices.question === "string"
                ? json.choices.question
                : "",
            options,
            ...(json.choices.multiple === true ? {multiple: true} : {}),
          },
        };
      }
    }
    const nested = json.error;
    const rawMessage =
      (typeof nested === "string" ? nested : nested?.message) ?? json.message;
    const message = rawMessage?.trim();
    const code =
      (typeof nested === "object" && nested ? nested.code : undefined) ??
      json.code;
    if (message || (typeof code === "number" && code !== 0)) {
      return {type: "error", code, message};
    }
    return {type: "delta", delta: ""};
  } catch {
    return {type: "delta", delta: ""};
  }
};

const applyEvent = (
  data: string,
  onDelta: (delta: string) => void,
  onAction?: (action: ChatAction) => void,
  onChoices?: (choices: ChatChoices) => void,
) => {
  const event = readSseData(data);
  if (event.type === "done") {
    return true;
  }
  if (event.type === "action") {
    onAction?.(event.action);
    return false;
  }
  if (event.type === "choices") {
    onChoices?.(event.choices);
    return false;
  }
  if (event.type === "error") {
    throw new ChatRequestError(event.code, {
      code: event.code,
      message: event.message,
    });
  }
  if (event.delta) {
    onDelta(event.delta);
  }
  return false;
};

const authHeaders = () => {
  const state = store.getState();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "text/event-stream",
  };
  const accessToken = state.auth.accessToken;
  const tenantId = state.app.tenant?.id;
  const isSuperAdmin = state.auth.user?.role === "ADMIN";
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }
  if (isSuperAdmin && tenantId) {
    headers["X-Sw-Tenant-Id"] = tenantId;
  }
  return headers;
};

const readResponseDto = async (response: globalThis.Response) => {
  try {
    return (await response.json()) as Response<ChatCompletion> & {
      message?: string;
    };
  } catch {
    return undefined;
  }
};

const streamChatMessage = async (
  request: ChatCompletionRequest,
  onDelta: (delta: string) => void,
  signal?: AbortSignal,
  onAction?: (action: ChatAction) => void,
  onChoices?: (choices: ChatChoices) => void,
) => {
  const base = process.env.REACT_APP_API_URL || "";
  const body = completionBody(request);
  let response: globalThis.Response;
  try {
    response = await fetch(`${base}${CHAT_STREAM_URL}`, {
      method: "POST",
      credentials: "include",
      headers: authHeaders(),
      body: JSON.stringify(body),
      signal,
    });
  } catch (error) {
    if (signal?.aborted) {
      throw error;
    }
    throw new ChatRequestError();
  }

  if (response.status === 404 || response.status === 405) {
    throw new ChatStreamUnsupported();
  }

  if (!response.ok) {
    const dto = await readResponseDto(response);
    throw new ChatRequestError(response.status, dto);
  }

  const contentType = response.headers.get("content-type") ?? "";
  if (contentType.includes("application/json")) {
    const dto = await readResponseDto(response);
    if (!dto || dto.code !== 0) {
      throw new ChatRequestError(response.status, dto);
    }
    if (dto.data?.content) {
      onDelta(dto.data.content);
    }
    if (dto.data?.action) {
      onAction?.(dto.data.action);
    }
    if (dto.data?.choices) {
      onChoices?.(dto.data.choices);
    }
    return;
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new ChatRequestError();
  }

  const decoder = new TextDecoder();
  let buffer = "";
  let reading = true;
  let finished = false;
  while (reading && !finished) {
    const {done, value} = await reader.read();
    if (done) {
      reading = false;
    } else {
      buffer += decoder.decode(value, {stream: true});
      const parsed = parseSseChunk(buffer);
      buffer = parsed.rest;
      finished = parsed.events.some(data =>
        applyEvent(data, onDelta, onAction, onChoices),
      );
    }
  }

  if (!finished && buffer.trim()) {
    const parsed = parseSseChunk(`${buffer}\n`);
    parsed.events.some(data => applyEvent(data, onDelta, onAction, onChoices));
  }
  if (finished) {
    await reader.cancel?.();
  }
};

export {
  streamChatMessage,
  parseSseChunk,
  readSseData,
  completionBody,
  choiceOptions,
  chatErrorDescriptor,
  isChatAccessError,
  isChatDisabledError,
  isChatbotOn,
  ChatStreamUnsupported,
  ChatRequestError,
};
